import { FC, MouseEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { Avatar, Box, Flex, Menu, MenuButton, Portal, rem, useDisclosure } from '@bonusly/design-system';
import { useBuildMonarchURL } from '@bonusly/url';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useIsRecognize } from '../../../hooks/use-is-recognize';
import { AppPath } from '../../admin-navbar/config/router';
import { useFetchNotifications } from '../../top-navbar/components/Notification/hooks/useFetchNotifications';
import { CompanyLogo } from './company-logo';
import { NotificationPanel } from './notification';
import { UserMobileNavMenu } from './user-mobile-nav-menu';

const MOBILE_NAVBAR_HEIGHT = 56;
const SPOOFING_BANNER_HEIGHT = 60;

interface UserMobileNavbarProps {
	isSpoofing?: boolean;
}

export const UserMobileNavbar: FC<UserMobileNavbarProps> = ({ isSpoofing = false }) => {
	const user = useCurrentUser();
	const menu = useDisclosure();
	const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
	const isRecognize = useIsRecognize();
	const monarchURL = useBuildMonarchURL();
	const homeURL = isRecognize ? monarchURL(AppPath.Home) : AppPath.Home;
	const { unreadCount } = useFetchNotifications(user.locale);

	const handleOpenNotifications = () => {
		menu.onClose();
		setIsNotificationPanelOpen(true);
	};

	const handleCloseNotifications = () => {
		setIsNotificationPanelOpen(false);
	};

	// Handle click for cross-domain navigation
	const handleLogoClick = (e: MouseEvent<HTMLAnchorElement>) => {
		if (isRecognize) {
			e.preventDefault();
			window.location.href = homeURL;
		}
	};

	return (
		<>
			<Flex
				alignItems="center"
				as="nav"
				bg="bg.surface"
				h={rem(MOBILE_NAVBAR_HEIGHT)}
				px={4}
				w="full"
				position="sticky"
				top={isSpoofing ? rem(SPOOFING_BANNER_HEIGHT) : 0}
				zIndex="sticky"
			>
				{/* Logo */}
				<Box as={Link} id="primary-nav-logo" h={rem(36)} to={homeURL} onClick={handleLogoClick}>
					<CompanyLogo />
				</Box>

				{/* Profile Avatar - triggers menu */}
				<Box ml="auto" display="flex" alignItems="center">
					<Menu placement="bottom-end" isOpen={menu.isOpen} onClose={menu.onClose} onOpen={menu.onOpen}>
						<MenuButton _active={{ transform: 'scale(0.95)' }} transitionDuration="normal" transitionProperty="common" aria-label="Open menu">
							<Box position="relative" display="flex" alignItems="center">
								<Avatar name={user.display_name} src={user.profile_pic_url} h={rem(40)} w={rem(40)} fontSize={1} />
								{unreadCount > 0 && <Box position="absolute" top={rem(-4)} right={rem(-4)} h={rem(10)} w={rem(10)} bg="red.500" borderRadius="full" />}
							</Box>
						</MenuButton>

						<Portal>
							<UserMobileNavMenu onOpenNotifications={handleOpenNotifications} />
						</Portal>
					</Menu>
				</Box>
			</Flex>

			{/* Notification Panel */}
			<NotificationPanel isOpen={isNotificationPanelOpen} onClose={handleCloseNotifications} isSidebarExpanded={false} />
		</>
	);
};
